/**
 * Layout du site vitrine : header sticky (nav par ancres + « Utiliser CIVIK »
 * toujours visible), footer complet, coquille des pages secondaires (fil
 * d'Ariane + H1 unique) et apparition progressive au scroll (respecte
 * prefers-reduced-motion, contenu visible sans JavaScript).
 */
import React, { useEffect, useRef, useState } from 'react';
import { NAV, FOOTER, APP_URL, CONTACT_EMAIL, DPO_EMAIL, SITE_URL } from './content';
import { Icon, LogoMark, Wordmark } from './icons';
import { metaFor } from './seo';

/* ── Apparition progressive ───────────────────────────────────────────── */
export function useRevealEffect(route) {
  useEffect(() => {
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return undefined;
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const nodes = Array.from(document.querySelectorAll('.reveal:not(.is-visible)'));
    if (reduce) { nodes.forEach((n) => n.classList.add('is-visible')); return undefined; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('is-visible'); io.unobserve(e.target); } });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    nodes.forEach((n) => io.observe(n));
    return () => io.disconnect();
  }, [route]);
}

/* ── Header ───────────────────────────────────────────────────────────── */
export function Header({ route }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const panelRef = useRef(null);

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 12);
    h();
    window.addEventListener('scroll', h, { passive: true });
    return () => window.removeEventListener('scroll', h);
  }, []);
  useEffect(() => { setOpen(false); }, [route]);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    document.body.classList.add('lp-menu-open');
    return () => { document.removeEventListener('keydown', onKey); document.body.classList.remove('lp-menu-open'); };
  }, [open]);

  const onHome = route === '/';
  const links = NAV.map((l) => ({ ...l, href: onHome ? l.href.replace(/^\//, '') : l.href }));

  return (
    <header className={`lp-header${scrolled ? ' is-scrolled' : ''}`}>
      <a className="lp-skip" href="#contenu">Aller au contenu</a>
      <nav className="lp-nav" aria-label="Navigation principale">
        <a href="/" className="lp-logo" aria-label="CIVIK – accueil">
          <LogoMark size={34} className="lp-logo-mark" />
          <Wordmark className="lp-logo-text" />
        </a>
        <ul className="lp-nav-links" id="lp-nav-desktop">
          {links.map((l) => <li key={l.label}><a href={l.href}>{l.label}</a></li>)}
        </ul>
        <div className="lp-nav-actions">
          <a href={APP_URL} className="lp-btn lp-btn-ghost lp-nav-login">Connexion</a>
          <a href={APP_URL} className="lp-btn lp-btn-primary lp-nav-cta" data-track="cta_utiliser_civik" data-track-place="header">Utiliser CIVIK</a>
          <button
            type="button"
            className="lp-burger"
            aria-expanded={open}
            aria-controls="lp-nav-mobile"
            aria-label={open ? 'Fermer le menu' : 'Ouvrir le menu'}
            onClick={() => setOpen((o) => !o)}
          >
            <Icon name={open ? 'close' : 'menu'} size={26} />
          </button>
        </div>
      </nav>
      <div id="lp-nav-mobile" ref={panelRef} className={`lp-mobile-panel${open ? ' is-open' : ''}`} hidden={!open}>
        <ul>
          {links.map((l) => <li key={l.label}><a href={l.href} onClick={() => setOpen(false)}>{l.label}</a></li>)}
        </ul>
        <div className="lp-mobile-actions">
          <a href={APP_URL} className="lp-btn lp-btn-primary lp-btn-block" data-track="cta_utiliser_civik" data-track-place="menu-mobile">Utiliser CIVIK</a>
          <a href={APP_URL} className="lp-btn lp-btn-outline lp-btn-block">Connexion à mon espace</a>
        </div>
      </div>
    </header>
  );
}

/* ── Footer ───────────────────────────────────────────────────────────── */
export function Footer() {
  return (
    <footer className="lp-footer" role="contentinfo">
      <div className="lp-container">
        <div className="lp-footer-grid">
          <div className="lp-footer-brand">
            <a href="/" className="lp-logo lp-logo-footer" aria-label="CIVIK – accueil">
              <LogoMark size={40} className="lp-logo-mark" />
              <Wordmark className="lp-logo-text" />
            </a>
            <p className="lp-footer-tagline">{FOOTER.tagline}</p>
            <p className="lp-footer-editor">Édité par MMI Industries<br /><a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a></p>
          </div>
          <nav className="lp-footer-col" aria-labelledby="footer-nav">
            <h2 id="footer-nav">Navigation</h2>
            <ul>{FOOTER.nav.map((l) => <li key={l.href}><a href={l.href}>{l.label}</a></li>)}</ul>
          </nav>
          <nav className="lp-footer-col" aria-labelledby="footer-product">
            <h2 id="footer-product">Application</h2>
            <ul>{FOOTER.product.map((l) => <li key={l.href}><a href={l.href}>{l.label}</a></li>)}</ul>
          </nav>
          <nav className="lp-footer-col" aria-labelledby="footer-legal">
            <h2 id="footer-legal">Juridique</h2>
            <ul>{FOOTER.legal.map((l) => <li key={l.href}><a href={l.href}>{l.label}</a></li>)}</ul>
          </nav>
        </div>
        <div className="lp-footer-bottom">
          <p>© {new Date().getFullYear()} CIVIK — MMI Industries — Tous droits réservés.</p>
          <p className="lp-footer-emergency">CIVIK ne remplace ni la police, ni la gendarmerie, ni les services de secours. Urgence : <a href="tel:17">17</a> · <a href="tel:18">18</a> · <a href="tel:112">112</a></p>
        </div>
      </div>
    </footer>
  );
}

/* ── Coquille des pages secondaires ───────────────────────────────────── */
export function Breadcrumb({ items }) {
  if (!items || !items.length) return null;
  return (
    <nav className="lp-breadcrumb" aria-label="Fil d'Ariane">
      <ol>
        <li><a href="/">Accueil</a></li>
        {items.map((it, i) => (
          <li key={it.url} aria-current={i === items.length - 1 ? 'page' : undefined}>
            {i === items.length - 1 ? <span>{it.name}</span> : <a href={it.url.replace(SITE_URL, '')}>{it.name}</a>}
          </li>
        ))}
      </ol>
    </nav>
  );
}

export function PageShell({ route, title, lead, kicker, children, wide }) {
  const meta = metaFor(route);
  return (
    <>
      <Header route={route} />
      <main id="contenu" className={`lp-page${wide ? ' lp-page-wide' : ''}`}>
        <div className="lp-page-head">
          <div className="lp-container">
            <Breadcrumb items={meta.breadcrumb} />
            {kicker ? <p className="lp-kicker">{kicker}</p> : null}
            <h1>{title}</h1>
            {lead ? <p className="lp-lead">{lead}</p> : null}
          </div>
        </div>
        <div className="lp-container lp-page-body">{children}</div>
      </main>
      <Footer />
    </>
  );
}

export { CONTACT_EMAIL, DPO_EMAIL };
